/**
 * @vitest-environment jsdom
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { materialize, type SCurveParams } from './engine/scurve'
import { formatByUnit } from './projectionsFormat'

type SCurveModalProps = {
  lineLabel: string
  valueUnit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct'
  initialParams: SCurveParams
  onCancel: () => void
  onConfirm: (params: SCurveParams) => void
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function SCurvePreview({ params }: { params: SCurveParams }) {
  const values = useMemo(() => materialize(params), [params])
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const width = 280
  const height = 72
  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * width
      const y = height - ((value - min) / range) * (height - 4) - 2
      return `${x},${y}`
    })
    .join(' ')

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="h-[72px] w-full rounded border border-gray-200 bg-gray-50"
      role="img"
      aria-label="S-curve preview across 36 months"
    >
      <polyline fill="none" stroke="currentColor" strokeWidth="1.5" className="text-brand-600" points={points} />
    </svg>
  )
}

export default function SCurveModal({
  lineLabel,
  valueUnit,
  initialParams,
  onCancel,
  onConfirm,
}: SCurveModalProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const [startText, setStartText] = useState(String(initialParams.start))
  const [endText, setEndText] = useState(String(initialParams.end))
  const [midpointText, setMidpointText] = useState(String(initialParams.midpoint))
  const [steepnessText, setSteepnessText] = useState(String(initialParams.steepness))

  const parsedParams = useMemo((): SCurveParams | null => {
    const start = Number(startText)
    const end = Number(endText)
    const midpoint = Number(midpointText)
    const steepness = Number(steepnessText)
    if (![start, end, midpoint, steepness].every(Number.isFinite)) {
      return null
    }
    if (midpoint < 1 || midpoint > 36 || steepness <= 0) {
      return null
    }
    return { start, end, midpoint, steepness }
  }, [endText, midpointText, startText, steepnessText])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) {
      return
    }
    const focusables = () =>
      Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      )
    focusables()[0]?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancel()
        return
      }
      if (event.key !== 'Tab') {
        return
      }
      const items = focusables()
      if (items.length === 0) {
        return
      }
      const first = items[0]!
      const last = items[items.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  const formatField = (raw: string, setter: (value: string) => void) => {
    const num = Number(raw)
    if (!Number.isFinite(num)) {
      return
    }
    setter(String(num))
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId} className="text-sm font-semibold text-gray-900">
          S-curve — {lineLabel}
        </h3>
        <div className="mt-4 space-y-3">
          <label className="block text-xs text-gray-600">
            Start
            <input
              className="input mt-1 box-border h-8 w-full px-2 text-sm tabular-nums"
              value={startText}
              onChange={(e) => setStartText(e.target.value)}
              onBlur={() => formatField(startText, setStartText)}
            />
            <span className="mt-0.5 block text-[10px] text-gray-500">
              Last actual: {formatByUnit(valueUnit, initialParams.start)}
            </span>
          </label>
          <label className="block text-xs text-gray-600">
            Steady state
            <input
              className="input mt-1 box-border h-8 w-full px-2 text-sm tabular-nums"
              value={endText}
              onChange={(e) => setEndText(e.target.value)}
              onBlur={() => formatField(endText, setEndText)}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-gray-600">
              Midpoint month (1–36)
              <input
                className="input mt-1 box-border h-8 w-full px-2 text-sm tabular-nums"
                value={midpointText}
                min={1}
                max={36}
                onChange={(e) => setMidpointText(e.target.value)}
              />
            </label>
            <label className="block text-xs text-gray-600">
              Steepness
              <input
                className="input mt-1 box-border h-8 w-full px-2 text-sm tabular-nums"
                value={steepnessText}
                onChange={(e) => setSteepnessText(e.target.value)}
              />
            </label>
          </div>
          {parsedParams ? <SCurvePreview params={parsedParams} /> : null}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={!parsedParams}
            onClick={() => parsedParams && onConfirm(parsedParams)}
          >
            OK
          </Button>
        </div>
      </div>
    </div>
  )
}
